import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type {
  EdfFileError,
  EdfParseProgress,
  ParsedEdfInfo,
} from '@lmt/signal-viewer-processor';
import {
  type NotificationId,
  useNotifications,
} from '../components/notifications/NotificationProvider';
import type { FileUploadData } from '../utils/file-upload';
import {
  showFileUploadErrorNotification,
  showOverlapNotificationIfNeeded,
  showParseErrorNotifications,
  showUploadSuccessNotification,
} from './upload-notifications';

type PendingUploadNotification = {
  id: NotificationId;
  fileName: string;
  expectedSignalCount: number;
};

export type UploadProgressReporter = (progress: number) => void;

type UseUploadMutationOptions<TResult> = {
  mutationFn: (
    uploadedData: FileUploadData,
    reportProgress: UploadProgressReporter,
  ) => Promise<TResult>;
  onUploadStart?: (uploadedData: FileUploadData) => void;
  onUploadSuccess: (result: TResult, uploadedData: FileUploadData) => void;
  onUploadError?: (error: unknown, uploadedData: FileUploadData) => void;
};

const parsingProgressStart = 20;

export function useUploadMutation<TResult>({
  mutationFn,
  onUploadStart,
  onUploadSuccess,
  onUploadError,
}: UseUploadMutationOptions<TResult>) {
  const notifications = useNotifications();
  const [pendingUploadNotification, setPendingUploadNotification] =
    useState<PendingUploadNotification | null>(null);
  const uploadNotificationIdRef = useRef<NotificationId | null>(null);

  const reportProgress = useCallback(
    (progress: number) => {
      const notificationId = uploadNotificationIdRef.current;
      if (!notificationId) return;

      notifications.updateUpload(notificationId, { progress });
    },
    [notifications],
  );

  const uploadMutation = useMutation<
    TResult,
    unknown,
    FileUploadData,
    { notificationId: NotificationId }
  >({
    mutationFn: (uploadedData) => mutationFn(uploadedData, reportProgress),
    onMutate: (uploadedData) => {
      onUploadStart?.(uploadedData);

      const notificationId = notifications.showUpload({
        title: 'Uploading file...',
        progress: 1,
      });
      uploadNotificationIdRef.current = notificationId;

      setPendingUploadNotification({
        id: notificationId,
        fileName: uploadedData.sourceFileName,
        expectedSignalCount: uploadedData.edfFiles.length,
      });

      return { notificationId };
    },
    onSuccess: (result, uploadedData) => {
      onUploadSuccess(result, uploadedData);
    },
    onError: (error, uploadedData, context) => {
      onUploadError?.(error, uploadedData);

      if (context?.notificationId) {
        notifications.close(context.notificationId);
      }

      uploadNotificationIdRef.current = null;
      setPendingUploadNotification(null);
      showFileUploadErrorNotification(notifications, error);
    },
  });

  const uploadFiles = useCallback(
    async (uploadedData: FileUploadData) => {
      await uploadMutation.mutateAsync(uploadedData).catch(() => undefined);
    },
    [uploadMutation],
  );

  const handleParseProgress = useCallback(
    (progress: EdfParseProgress) => {
      if (!pendingUploadNotification || progress.totalFiles <= 0) return;

      notifications.updateUpload(pendingUploadNotification.id, {
        progress:
          parsingProgressStart +
          (progress.completedFiles / progress.totalFiles) *
            (100 - parsingProgressStart),
      });
    },
    [notifications, pendingUploadNotification],
  );

  const completeUploadNotification = useCallback(
    (parsedInfo: ParsedEdfInfo[]) => {
      const uploadNotificationId = pendingUploadNotification?.id;
      if (
        !uploadNotificationId ||
        parsedInfo.length < pendingUploadNotification.expectedSignalCount
      ) {
        return;
      }

      notifications.updateUpload(uploadNotificationId, { progress: 100 });
      showUploadSuccessNotification(
        notifications,
        pendingUploadNotification.fileName,
      );
      showOverlapNotificationIfNeeded(notifications, parsedInfo);

      uploadNotificationIdRef.current = null;
      setPendingUploadNotification(null);
    },
    [notifications, pendingUploadNotification],
  );

  const handleParseErrors = useCallback(
    (errors: EdfFileError[]) => {
      if (pendingUploadNotification) {
        notifications.close(pendingUploadNotification.id);
      }

      uploadNotificationIdRef.current = null;
      setPendingUploadNotification(null);
      showParseErrorNotifications(notifications, errors);
    },
    [notifications, pendingUploadNotification],
  );

  return {
    uploadFiles,
    handleParseProgress,
    completeUploadNotification,
    handleParseErrors,
  };
}
